import { useRegisterSW } from 'virtual:pwa-register/react';
import { useEffect, useRef, useState } from 'react';
import { BUILD_VERSION } from '@/lib/api';
import { flushOutbox } from '@/lib/outbox';
import { useSignals } from '@/lib/signals';
import {
  applyUpdate,
  buildBelowMin,
  closeLiveStreams,
  setUpdateWaiting,
  UPDATE_DISCOVER_MS,
  useUpdateHeld,
  waitForWaitingWorker,
} from '@/lib/update';
import { useOutboxCount } from '@/lib/useOutbox';
import { useConfig } from './session';
import { UpdateView } from './UpdateView';

/**
 * Update prompt. registerType is 'prompt': a waiting worker activates only when the person
 * taps, never by itself, so code never swaps under a scorekeeper mid-possession.
 *
 * - A waiting worker shows "New version on the bench" with Sub it in and Later. While a hold is
 *   active (this phone keeps the book of a live game) it stays away and the Me tab shows a dot.
 * - A 426 from any call, or a config.minClient above this build, shows the blocking card. The
 *   outbox keeps every row through it, and the card says how many plays are waiting.
 * - The tap runs applyUpdate (lib/update.ts): close streams, flush the outbox, activate, reload.
 *   A worker still downloading is waited for ("Downloading the update..."), never raced by a reload.
 * - A new build is looked for whenever the app comes back to the front, and when signal returns
 *   (iOS keeps an installed app in memory for days).
 */
export function UpdatePrompt() {
  const held = useUpdateHeld();
  const config = useConfig();
  const waitingPlays = useOutboxCount();
  const forced = useSignals((s) => s.updateRequired) || buildBelowMin(BUILD_VERSION, config.data?.minClient);
  const [working, setWorking] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [stalled, setStalled] = useState(false);
  const [stuck, setStuck] = useState(false);
  const registration = useRef<ServiceWorkerRegistration | null>(null);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, reg) {
      if (!reg) return;
      registration.current = reg;
      const check = () => {
        if (document.visibilityState === 'visible' && navigator.onLine !== false) void reg.update().catch(() => {});
      };
      document.addEventListener('visibilitychange', check);
      window.addEventListener('online', check);
    },
  });

  useEffect(() => setUpdateWaiting(needRefresh), [needRefresh]);

  useEffect(() => {
    if (forced && !needRefresh) {
      // 426 with no waiting worker yet: ask the browser to look for one.
      void navigator.serviceWorker?.getRegistration().then((r) => r?.update());
    }
  }, [forced, needRefresh]);

  const apply = async () => {
    setWorking(true);
    setStuck(false);
    setStalled(false);
    setDownloading(false);
    const getReg = async () => registration.current ?? (await navigator.serviceWorker?.getRegistration()) ?? null;
    const outcome = await applyUpdate(
      {
        closeStreams: closeLiveStreams,
        flush: flushOutbox,
        activate: () => updateServiceWorker(true),
        reload: () => window.location.reload(),
        checkForWaiting: async (onDownloading) => {
          const reg = await getReg();
          if (!reg) return 'none';
          return waitForWaitingWorker(reg, UPDATE_DISCOVER_MS, undefined, { onDownloading });
        },
        onDownloading: () => setDownloading(true),
        unregister: async () => {
          // Forced update and no new worker anywhere: the old worker would answer the reload from
          // its precached index.html and the 426 card would come straight back. Outbox rows live
          // in IndexedDB, which this does not touch.
          await (await getReg())?.unregister();
          const names = await caches.keys();
          await Promise.all(names.filter((n) => n.includes('precache')).map((n) => caches.delete(n)));
        },
        sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
      },
      needRefresh,
      { forced },
    );
    setDownloading(false);
    setWorking(false);
    // Only reached when the reload did not take the page away. Give it a moment before saying so.
    if (outcome === 'downloading') setStalled(true);
    else window.setTimeout(() => setStuck(true), 2500);
  };

  return (
    <UpdateView
      forced={forced}
      needRefresh={needRefresh}
      held={held}
      waitingPlays={waitingPlays}
      working={working}
      downloading={downloading}
      stalled={stalled}
      stuck={stuck}
      onApply={apply}
      onLater={() => setNeedRefresh(false)}
    />
  );
}
